// 3rd party libraries
import angular from 'angular';
import momentTimezone from 'moment-timezone';
import 'angular-moment';

// Project libraries
import { isValidDateObject } from '../dateutil';
import { isString } from '../util';

// TODO: Pull this data from server
const serverTimezoneName = 'America/Chicago';

class DateTimeService {
  constructor(
    moment,
    LogService
  ) {
    "ngInject";
    this.moment = moment;
    this.LogService = LogService;

    this.localTimezoneName = this.moment.tz.guess();
    this.serverTimezoneName = serverTimezoneName;
  }

  set serverTimezoneName(name) {
    this._defaultTimezoneName = name;
  }

  get serverTimezoneName() {
    return this._defaultTimezoneName;
  }

  set localTimezoneName(name) {
    this._localTimezoneName = name;
  }

  get localTimezoneName() {
    return this._localTimezoneName;
  }

  defaultTimezoneNameForEvent(event) {
    let timezoneName;
    if (event && event.venue_timezone) {
      timezoneName = event.venue_timezone;
    } else {
      timezoneName = this.serverTimezoneName
    }
    return timezoneName;
  }

  /**
   * @param timezoneName
   * @returns {Object} zone - moment-timezone Zone object
   * @returns {string} zone.name - Name of the timezone
   * @returns {function} zone.offset - Function that calculates UTC offset for a given timestamp
   */
  timezoneInfo(timezoneName) {
    return this.moment.tz.zone(timezoneName);
  }

  verifyTimezoneName(timezoneName, fallback = null) {
    let zone = this.timezoneInfo(timezoneName);
    if (!zone && fallback) {
      zone = this.timezoneInfo(fallback);
    }
    if (zone) {
      return zone.name;
    } else {
      return null;
    }
  }

  isValidDateObject(object) {
    return isValidDateObject(object);
  }

  parseDateWithServerTimezone(dateObjectOrString) {
    return this.parseDateWithTimezone(dateObjectOrString, this.serverTimezoneName);
  }

  parseDateWithLocalTimezone(dateObjectOrString) {
    return this.parseDateWithTimezone(dateObjectOrString, this.localTimezoneName);
  }

  parseDateWithTimezone(dateObjectOrString, timezoneName) {
    return this.parseDate(dateObjectOrString, timezoneName);
  }

  parseDateWithTimezoneOrServerTimezone(dateObjectOrString, timezoneName = null) {
    const useTimezoneName = timezoneName || this.serverTimezoneName;
    return this.parseDateWithTimezone(dateObjectOrString, useTimezoneName);
  }

  parseMomentWithTimezone(dateObjectOrString, timezoneName = null) {
    return this.getMomentForDateObjectOrString(dateObjectOrString, timezoneName);
  }

  /**
   * Whole calendar days from today until the given expiration date, clamped at
   * a minimum of 0 so it never goes negative once the date has passed. Returns
   * null when no/invalid date is given.
   *
   * Computed live on the device (rather than baked into the synced payload) so
   * the countdown stays current offline. Mirrors the server-side rule in
   * Merchandising\Activity\ExpirationHelper::daysRemaining() so the staff app
   * and the admin views report the same number.
   *
   * @param {string|Date} expirationDateOrString
   * @param {string|null} timezoneName  Timezone for "today"; falls back to local.
   * @returns {number|null}
   */
  daysUntil(expirationDateOrString, timezoneName = null) {
    if (!expirationDateOrString) {
      return null;
    }
    const tz         = timezoneName || this.localTimezoneName;
    const expiration = this.getMomentForDateObjectOrString(expirationDateOrString, tz);
    if (!expiration || !expiration.isValid()) {
      return null;
    }
    const today = this.moment.tz(tz).startOf('day');
    const days  = expiration.clone().startOf('day').diff(today, 'days');
    return Math.max(0, days);
  }

  getMomentForDateObjectOrString(dateObjectOrString, timezoneName = null) {
    let moment;

    if (this.moment.isMoment(dateObjectOrString)) {
      moment = dateObjectOrString;
    } else if (isValidDateObject(dateObjectOrString)) {
      moment = this.moment(dateObjectOrString);
    } else {
      try {
        if (timezoneName) {
          // Use moment-timezone to parse date with the specified default timezone.
          moment = this.moment.tz(dateObjectOrString, timezoneName);
        } else {
          moment = this.moment(dateObjectOrString);
        }
      } catch (e) {
        this.LogService.warn(`Error parsing date into moment. Object:`, dateObjectOrString, `error:`, e);
        moment = null;
      }
    }

    // Always convert to specified timezone if present
    if (moment && timezoneName) {
      moment = moment.tz(timezoneName);
    }

    return moment;
  }

  parseDate(dateObjectOrString, defaultTimezone = null) {
    let dateObject;
    if (!dateObjectOrString) {
      dateObject = null;
    } else if (isValidDateObject(dateObjectOrString)) {
      dateObject = dateObjectOrString;
    } else {
      const parsedMoment = this.getMomentForDateObjectOrString(dateObjectOrString, defaultTimezone);
      if (parsedMoment && parsedMoment.isValid()) {
        dateObject = parsedMoment.toDate();
      } else {
        dateObject = null;
      }
    }
    return dateObject;
  }

  /**
   * Shift a date object for display in a different timezone.
   *
   * Useful when a date/time is parsed in local browser timezone, but should be displayed as if it were in a different
   * timezone.
   *
   * Example:
   *   date: 2017-03-01 12:00:00 PST (-0800)
   *   sourceTimezone: America/Los_Angeles (PST -0800)
   *   destTimezone: America/Chicago (CST -0600)

   *   We assume the date is "correct" in the sourceTimezone, and we shift it so that it appears as if it were in the
   *   destTimezone.
   *
   *   The output should be 2017-03-01 14:00:00 PST (-0800)
   */
  shiftDateObject(date, sourceTimezoneName, destTimezoneName) {
    const sourceZone = this.timezoneInfo(sourceTimezoneName);
    const destZone = this.timezoneInfo(destTimezoneName);

    if (!isValidDateObject(date)) {
      this.LogService.warn(`shiftDateObject() called with invalid date object:`, date);
      return date;
    }

    if (!sourceZone) {
      this.LogService.warn(`shiftDateObject() called with invalid sourceTimezoneName: ${sourceTimezoneName}`);
      return date;
    }
    if (!destZone) {
      this.LogService.warn(`shiftDateObject() called with invalid destTimezoneName: ${destTimezoneName}`);
      return date;
    }

    const timestamp = date.getTime();
    const sourceOffset = sourceZone.utcOffset(timestamp);
    const destOffset = destZone.utcOffset(timestamp);
    const differenceInMinutes = sourceOffset - destOffset;
    const differenceInMS = differenceInMinutes * 60 * 1000;
    const shiftedTimestamp = timestamp + differenceInMS;
    const shiftedDate = new Date(shiftedTimestamp);

    return shiftedDate;
  }

  /**
   * Reverse of shiftDateObject()
   */
  unshiftDateObject(date, sourceTimezoneName, destTimezoneName) {
    return this.shiftDateObject(date, destTimezoneName, sourceTimezoneName);
  }

  /**
   * Wrapper for shiftDateObject that assumes sourceTimezoneName is local browser timezone
   */
  shiftDateObjectFromLocal(date, destTimezoneName) {
    return this.shiftDateObject(date, this.localTimezoneName, destTimezoneName);
  }

  shiftDateObjectFromLocalToTimezoneOrServerTimezone(date, destTimezoneName = null) {
    if (!destTimezoneName) {
      destTimezoneName = this.serverTimezoneName;
    }
    return this.shiftDateObject(date, this.localTimezoneName, destTimezoneName);
  }

  /**
   * Wrapper for unshiftDateObject that assumes sourceTimezoneName is local browser timezone
   */
  unshiftDateObjectFromLocal(date, destTimezoneName) {
    return this.unshiftDateObject(date, this.localTimezoneName, destTimezoneName);
  }

  parseDateTimeStringFromAPI(dateTimeStringOrDateObject, defaultTimezoneName = null) {
    return this.parseDate(dateTimeStringOrDateObject, defaultTimezoneName);
  }

  parseTimeWithServerTimezone(timeStringOrObject, relativeToDateStringOrObject = null) {
    return this.parseTimeWithTimezone(timeStringOrObject, relativeToDateStringOrObject, this.serverTimezoneName);
  }

  parseTimeWithLocalTimezone(timeStringOrObject, relativeToDateStringOrObject = null) {
    return this.parseTimeWithTimezone(timeStringOrObject, relativeToDateStringOrObject, this.localTimezoneName);
  }

  parseTimeWithTimezoneOrServerTimezone(timeStringOrObject, relativeToDateStringOrObject = null, timezoneName = null) {
    const useTimezoneName = timezoneName || this.serverTimezoneName;
    return this.parseTimeWithTimezone(timeStringOrObject, relativeToDateStringOrObject, useTimezoneName);
  }

  parseTimeWithTimezone(timeStringOrObject, relativeToDateStringOrObject = null, timezoneName = null) {
    return this.parseTime(timeStringOrObject, relativeToDateStringOrObject, timezoneName);
  }

  parseTime(timeStringOrObject, relativeToDateStringOrObject = null, defaultTimezone = null) {
    let relativeToDateObject;
    if (relativeToDateStringOrObject) {
      relativeToDateObject = this.parseDate(relativeToDateStringOrObject, defaultTimezone);
      if (!relativeToDateObject) {
        this.LogService && this.LogService.warn(`DateTimeService.parseTime() unable to parse relativeToDateStringOrObject: ${relativeToDateStringOrObject}`);
      }
    }

    let dateObject = null;
    if (isString(timeStringOrObject)) {
      // First attempt to parse as time
      try {
        const timeFormats = ['h:m a', 'H:m'];
        let parsedMoment;
        if (defaultTimezone) {
          parsedMoment = this.moment.tz(timeStringOrObject, timeFormats, defaultTimezone);
        } else {
          parsedMoment = this.moment(timeStringOrObject, timeFormats);
        }
        if (parsedMoment.isValid()) {
          dateObject = parsedMoment.toDate();
        }
      } catch (e) {
        this.LogService.debug("Unable to parse as time:", timeStringOrObject);
      }
    }

    if (!dateObject) {
      // Attempt to parse as date
      dateObject = this.parseDate(timeStringOrObject, defaultTimezone);
    }

    if (dateObject && relativeToDateObject) {
      dateObject.setFullYear(relativeToDateObject.getFullYear());
      dateObject.setMonth(relativeToDateObject.getMonth());
      dateObject.setDate(relativeToDateObject.getDate());
    }

    this.LogService.debug(`parseTime(${timeStringOrObject}, ${relativeToDateStringOrObject}, ${defaultTimezone}) returning`, dateObject);

    return dateObject;
  }

  parseTimeStringFromAPI(timeString, relativeToDate = null, defaultTimezone = null) {
    return this.parseTime(timeString, relativeToDate, defaultTimezone);
  }

  serializeDateTimeForAPI(dateObject, destTimezoneName = null) {
    let serialized = null;
    let moment = this._convertDateObjectToMomentForSerialization(dateObject, destTimezoneName);
    if (moment) {
      serialized = moment.format('YYYY-MM-DD HH:mm:ss');
    }
    return serialized;
  }

  serializeDateTimeForAPIWithTimezone(dateObject, destTimezoneName = null) {
    return this.serializeDateTimeForAPI(dateObject, destTimezoneName);
  }

  serializeDateTimeForAPIWithTimezoneOrServerTimezone(dateObject, destTimezoneName = null) {
    let useTimezoneName = destTimezoneName || this.serverTimezoneName;
    return this.serializeDateTimeForAPIWithTimezone(dateObject, useTimezoneName);
  }

  serializeDateForAPI(dateObject, destTimezoneName = null) {
    // A date-only string has no zone, so converting it can only move the day.
    if (isString(dateObject)) {
      const dateOnly = this._serializeDateOnlyString(dateObject);
      if (dateOnly !== undefined) {
        return dateOnly;
      }
    }
    let serialized = null;
    let moment = this._convertDateObjectToMomentForSerialization(dateObject, destTimezoneName);
    if (moment) {
      serialized = moment.format('YYYY-MM-DD');
    }
    return serialized;
  }

  /**
   * Normalizes a date-only string without any zone conversion: a four-digit year, then a one- or
   * two-digit month and day, each preceded by '-' or '/'; surrounding whitespace is ignored.
   *
   * @param {string} value
   * @returns {string|null|undefined} zero-padded 'YYYY-MM-DD'; null when the string has the
   *   date-only shape but is not a real calendar date; undefined when it is not date-only.
   */
  _serializeDateOnlyString(value) {
    const match = /^\s*(\d{4})[-/](\d{1,2})[-/](\d{1,2})\s*$/.exec(value);
    if (!match) {
      return undefined;
    }
    const padded = `${match[1]}-${match[2].padStart(2, '0')}-${match[3].padStart(2, '0')}`;
    return this.moment(padded, 'YYYY-MM-DD', true).isValid() ? padded : null;
  }

  serializeDateForAPIWithTimezone(dateObject, destTimezoneName = null) {
    return this.serializeDateForAPI(dateObject, destTimezoneName);
  }

  serializeDateForAPIWithTimezoneOrServerTimezone(dateObject, destTimezoneName = null) {
    let useTimezoneName = destTimezoneName || this.serverTimezoneName;
    return this.serializeDateForAPIWithTimezone(dateObject, useTimezoneName);
  }

  serializeTimeForAPI(dateObject, destTimezoneName = null) {
    let serialized = null;
    let moment = this._convertDateObjectToMomentForSerialization(dateObject, destTimezoneName)
    if (moment) {
      serialized = moment.format('hh:mm a');
    }
    return serialized;
  }

  serializeTimeForAPIWithTimezone(dateObject, destTimezoneName = null) {
    return this.serializeTimeForAPI(dateObject, destTimezoneName);
  }

  serializeTimeForAPIWithTimezoneOrServerTimezone(dateObject, destTimezoneName = null) {
    let useTimezoneName = destTimezoneName || this.serverTimezoneName;
    return this.serializeTimeForAPI(dateObject, useTimezoneName);
  }

  serializeDateTimeToIsoWithOffset(dateObject) {
    if (!isValidDateObject(dateObject)) {
      dateObject = this.parseDate(dateObject);
    }

    if (!isValidDateObject(dateObject)) {
      return null;
    }
    let momentDate = this.moment.tz(dateObject, this.localTimezoneName);
    let formattedDateTime = momentDate.format('YYYY-MM-DDTHH:mm:ssZ');

    return formattedDateTime;
  }

  /**
   * Converts a Date object into a moment object in the specified timezone, or in the server timezone if no timezone
   * is specified.
   *
   * If the input is not a Date object, it first attempts to parse into a Date object with the specified timezone.
   *
   * @param dateObject
   * @param destTimezoneName
   * @returns {*}
   * @private
   */
  _convertDateObjectToMomentForSerialization(dateObject, destTimezoneName = null) {
    if (!isValidDateObject(dateObject)) {
      dateObject = this.parseDateWithTimezoneOrServerTimezone(dateObject, destTimezoneName);
    }
    if (!isValidDateObject(dateObject)) {
      return null;
    }
    let dateMoment = this.moment(dateObject);
    if (destTimezoneName) {
      dateMoment.tz(destTimezoneName);
    } else if (this.serverTimezoneName) {
      // TODO: Determine if it's safe to never automatically convert to server timezone.
      // Instead, we should explicitly pass server timezone if that is the desire.

      // dateMoment.tz(this.serverTimezoneName);
    }
    return dateMoment;
  }
}

const moduleName = 'recaps.services.DateTimeService';
const module = angular.module(moduleName, [
  'angularMoment',
]);

module.service('DateTimeService', DateTimeService);

// Override moment with moment-timezone
module.constant('moment', momentTimezone);

export default module;
